import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import Switch from 'components/ui/switch';
import { Button } from 'components/ui/button';
import { useAuthContext } from 'modules/auth';
import * as Organization from 'modules/organization';
import { toast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import type { OrganizationPreferences as OrgPrefs } from '@examing/types';

/** Fetches the full org so a save merges, never drops, unrelated free-form settings keys. */
const OrganizationNotifications: React.FC = () => {
  const { t } = useTranslation();
  const { state: authState } = useAuthContext();
  const orgId = authState.organization?.id;

  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [notifications, setNotifications] = useState<OrgPrefs['notifications']>(
    Organization.DEFAULT_ORG_PREFERENCES.notifications,
  );
  const [baseline, setBaseline] = useState<OrgPrefs['notifications']>(
    Organization.DEFAULT_ORG_PREFERENCES.notifications,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!orgId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    Organization.Api.GetById(orgId)
      .then(({ data }: any) => {
        const bag = (data?.settings ?? {}) as Record<string, unknown>;
        const resolved = Organization.resolveOrgPreferences(bag).notifications;
        setSettings(bag);
        setNotifications(resolved);
        setBaseline(resolved);
      })
      .catch(() => toast.error(t('failed_to_load_organization')))
      .finally(() => setIsLoading(false));
  }, [orgId, t]);

  const setNotification = (key: keyof OrgPrefs['notifications'], value: boolean) =>
    setNotifications((prev) => ({ ...prev, [key]: value }));

  const isDirty = JSON.stringify(notifications) !== JSON.stringify(baseline);

  const handleSave = async () => {
    if (!orgId) return;
    setIsSaving(true);
    try {
      const nextSettings = { ...settings, notifications };
      await Organization.Api.Update(orgId, { settings: nextSettings });
      setSettings(nextSettings);
      setBaseline(notifications);
      toast.success(t('organization_updated_successfully'));
    } catch (err: any) {
      const message = err?.response?.data?.message || t('failed_to_load_organization');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl space-y-8">
      {/* No page-level heading here — this page is only hosted inside the
          settings dialog, whose section title (SettingsDialog.tsx) is the
          single source of truth for the "Notifications" heading. The
          subtitle still carries context the dialog doesn't show, and no
          longer re-animates on every section switch. */}
      <p className="text-muted-foreground">{t('manage_notification_preferences')}</p>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('notifications')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Switch
              checked={notifications.importantEvents}
              onChange={(v) => setNotification('importantEvents', v)}
              description={t('receive_email_alerts_for_important_events')}
            />
            <Switch
              checked={notifications.candidateCompletion}
              onChange={(v) => setNotification('candidateCompletion', v)}
              description={t('get_notified_when_candidates_complete_assessments')}
            />
            <Switch
              checked={notifications.testFeedback}
              onChange={(v) => setNotification('testFeedback', v)}
              description={t('get_notified_when_learners_leave_test_feedback')}
            />
            <Switch
              checked={notifications.weeklyDigest}
              onChange={(v) => setNotification('weeklyDigest', v)}
              description={t('receive_weekly_analytics_digest')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          disabled={!isDirty || isSaving}
          onClick={() => setNotifications(baseline)}
        >
          {t('cancel')}
        </Button>
        <Button className="w-full sm:w-auto" onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
          {t('save_changes_2')}
        </Button>
      </div>
    </div>
  );
};

export default OrganizationNotifications;
